import {
  CellContext,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  Row,
  RowSelectionState,
  SortingState,
  Table as TableType,
  useReactTable,
} from '@tanstack/react-table';
import classNames from 'classnames';
import { KeyboardEvent, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Checkbox from '@/components/Checkbox/Checkbox';
import inputStyles from '@/components/Input/Input.module.css';
import Tooltip from '@/components/Tooltip/Tooltip';
import sharedTableStyles from './CustomStyles.module.css';
import SortIcon from '@/components/Tables/SortIcon';
import styles from '@/components/Tables/Table/Table.module.css';
import { TableProps } from '@/components/Tables/Table/Table.types';
import { useTranslation } from 'react-i18next';

export const getAriaSort = (sortStatus: 'asc' | 'desc' | false) => {
  switch (sortStatus) {
    case 'asc':
      return 'ascending';
    case 'desc':
      return 'descending';
    default:
      return 'none';
  }
};

const SimpleTable = <TData extends object>({
  data,
  columns,
  returnedRecordsText,
  enableFilters = false,
  enableRowSelection = false,
  enableMultiRowSelection = true,
  getTableApi,
  selectedRows,
  onRowSelectionChange,
  isLoading,
  loadingRowCount = 15,
  width,
  height,
  includeMinWidth = false,
  className,
  headersToWrap,
  selectAllRows = false,
  meta,
}: TableProps<TData>) => {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(selectedRows || {});
  const id = useId();

  // if we have an onRowSelectionChange prop, we assume selection state is being managed externally (via the selectedRows prop)
  const getRowSelectionHandler = () => {
    if (onRowSelectionChange) return onRowSelectionChange;
    return setRowSelection;
  };

  // if we receive selectedRows from props, update our internal state
  useEffect(() => {
    if (selectedRows) {
      setRowSelection(selectedRows);
    }
  }, [selectedRows]);

  const renderCheckboxCell = useCallback(
    (params: { row: Row<unknown> }) => (
      <ul className={sharedTableStyles.rowCheckbox}>
        <li>
          <Checkbox
            label=""
            aria-label="select row"
            id={`${id}-row-${params.row.id}-checkbox`}
            checked={params.row.getIsSelected()}
            onChange={params.row.getToggleSelectedHandler()}
            disabled={!params.row.getCanSelect()}
          />
        </li>
      </ul>
    ),
    [id],
  );

  const renderHeaderCell = useCallback(
    (params: { table: TableType<unknown> }) => (
      <div className={sharedTableStyles.headerCheckbox}>
        <Checkbox
          label=""
          aria-label="select all rows"
          id={`${id}-header-checkbox`}
          checked={params.table.getIsAllRowsSelected()}
          onChange={params.table.getToggleAllRowsSelectedHandler()}
        />
      </div>
    ),
    [id],
  );

  const columnsWithRowSelection = useMemo(
    () => [
      {
        id: 'select',
        cell: renderCheckboxCell,
      },
      ...columns,
    ],
    [columns, enableMultiRowSelection, renderCheckboxCell, renderHeaderCell],
  );

  const table = useReactTable({
    data: data as any[],
    columns: columnsWithRowSelection as any[],
    getCoreRowModel: getCoreRowModel(),
    enableFilters,
    enableRowSelection,
    enableMultiRowSelection,
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters,
      rowSelection,
    },
    onSortingChange: setSorting,
    onRowSelectionChange: getRowSelectionHandler(),
    getSortedRowModel: getSortedRowModel(),
    meta,
  });

  useEffect(() => {
    table.toggleAllPageRowsSelected(selectAllRows);
  }, [selectAllRows]);

  const filteredRowCount = table.getFilteredRowModel().rows.length;
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [hasScrollbar, setHasScrollbar] = useState(false);

  const recordText = useMemo(() => {
    if (returnedRecordsText) {
      return returnedRecordsText(filteredRowCount);
    }
    return t('common.table.row', { count: filteredRowCount });
  }, [returnedRecordsText, filteredRowCount, t]);

  const checkForScrollbar = useCallback(() => {
    if (!tableContainerRef.current) return;
    if (tableContainerRef.current.clientWidth === tableContainerRef.current.scrollWidth) {
      setHasScrollbar(false);
    }
    if (tableContainerRef.current.clientWidth < tableContainerRef.current.scrollWidth) {
      setHasScrollbar(true);
    }
  }, []);

  useLayoutEffect(() => {
    checkForScrollbar();
  }, [isLoading, checkForScrollbar]);

  useEffect(() => {
    window.addEventListener('resize', checkForScrollbar);
    return () => window.removeEventListener('resize', checkForScrollbar);
  }, [checkForScrollbar]);

  return (
    <div className={classNames(styles.tableContainerWrapper, { [className!]: className !== undefined })}>
      <div
        style={{ width, height }}
        ref={tableContainerRef}
        className={classNames(styles.tableContainer, {
          [styles.loading]: isLoading,
          [sharedTableStyles.minWidth]: includeMinWidth,
          [sharedTableStyles.hasScrollbar]: hasScrollbar,
        })}
      >
        <table className={classNames(styles.table, { [styles.withRowSelection]: enableRowSelection })}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    data-testid={`table-header-${header.id}`}
                    key={header.id}
                    className={sharedTableStyles.columnHeader}
                    aria-sort={getAriaSort(header.column.getIsSorted())}
                  >
                    <div
                      data-testid={`table-sort-button-${header.id}`}
                      className={sharedTableStyles.columnHeaderTextAndIcon}
                    >
                      <span
                        className={classNames(sharedTableStyles.columnHeaderText, {
                          [sharedTableStyles.wrapHeaderText]: headersToWrap?.includes(`${header.column.columnDef.id}`),
                        })}
                      >
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row, index) => (
              <tr
                key={row.id}
                className={classNames(sharedTableStyles.row, {
                  [sharedTableStyles.even]: index % 2 === 0,
                  [sharedTableStyles.selectedRow]: row.getIsSelected(),
                })}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className={sharedTableStyles.cell}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {hasScrollbar && <div className={sharedTableStyles.scrollShadow} />}
    </div>
  );
};

export default SimpleTable;
